import { Box, ButtonBase, Divider, IconButton, Skeleton, Typography } from '@mui/material';
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AlertDialog from '../../component/dialog/alert-dialog.component';
import GlobalNotification from '../../component/common/snackbar';
import StackedFilterTabs, { type StackedFilterTabItem } from '../../component/tab/stacked-filter-tabs.component';
// import { firstMissingPositive } from '../../constants/utils';
import i18n from '../../i18n';
import { apiManage_rbp } from '../../request';
import LoadingOverlayButton from '../../component/button/loading-overlay-button.component';
import JobSettingsDetailPanel from './job-settings-detail-panel.component';
import JobSettingsAddEmployeeDialog from './job-settings-add-employee-dialog.component';
import JobSettingsExceptionPermissionDialog from './job-settings-exception-permission-dialog.component';
import {
    applyPersonalPermissionPatchToBody,
    computeJobSettingsExceptions,
    findEmployeeClassTemplateByRoleValue,
    isOverrideFormEqualToTemplate,
    listEnabledPermissionDefsForChips,
    revertPermissionKeyToTemplate,
    resolveEffectivePermissionApiRow,
} from './job-settings-personal-permission.utils';
import type { EmployeePermissionClassRow } from './permission-settings.types';
import { cloneJobSettingsEmployeeInfo, createEmptyJobSettingsEmployeeInfo } from './job-settings.data';
import {
    apiEmployeeToRow,
    buildClassNameByIdMap,
    jobSettingsFormToApiPatch,
    jobSettingsFormToApiPost,
    stripEmployeePatchPayload,
} from './job-settings.mapper';
import type {
    JobSettingsEmployeeClassRow,
    JobSettingsEmployeeInfo,
    JobSettingsEmployeeRow,
    JobSettingsRoleFilterValue,
} from './job-settings.types';
import { ReactComponent as PlusIcon } from '../../static/images/svg/ic_round-plus.svg';
import { ReactComponent as TablerEditIcon } from '../../static/images/svg/tabler_edit.svg';
import './scss/job-settings.page.scss';

function isApiError(res: unknown): res is { error: true } {
    return Boolean(res && typeof res === 'object' && 'error' in res && (res as { error?: boolean }).error);
}

/** POST 创建员工接口若返回 `employee_id`，用于与列表行 `id` 对齐 */
function readCreatedEmployeeIdFromPost(res: unknown): string | null {
    if (res == null || typeof res !== 'object') {
        return null;
    }
    const raw = (res as Record<string, unknown>).employee_id;
    if (typeof raw === 'number' && Number.isFinite(raw)) {
        return String(Math.trunc(raw));
    }
    if (typeof raw === 'string' && raw.trim() !== '') {
        return raw.trim();
    }
    return null;
}

/** 进入某行时右侧表单的快照，用于「取消」恢复 */
type JobSettingsDraftSnapshot = {
    roleValue: string;
    personalSupplement: boolean;
    personalPermissionOverride: EmployeePermissionClassRow | null;
    employeeInfo: JobSettingsEmployeeInfo;
};

/** 从列表行同步个人权限相关 state */
function clonePersonalPermissionOverride(
    row: JobSettingsEmployeeRow,
): EmployeePermissionClassRow | null {
    return row.defaultPersonalPermissionOverride
        ? _.cloneDeep(row.defaultPersonalPermissionOverride)
        : null;
}

/**
 * 解析右侧岗位下拉初始值：仅当员工 `employee_class` 在选项列表中时才选中，否则留空（不擅自回退首项）。
 * @param defaultRoleValue - 员工当前 `employee_class` 字符串
 * @param roleSelectItems - 岗位下拉选项
 */
function resolveJobSettingsRoleValueForSelect(
    defaultRoleValue: string,
    roleSelectItems: { value: string; label: string }[],
): string {
    if (defaultRoleValue === '' || defaultRoleValue == null) {
        return '';
    }
    if (roleSelectItems.some((item) => item.value === defaultRoleValue)) {
        return defaultRoleValue;
    }
    return '';
}

/**
 * 云 RBP 员工管理 — 岗位设置：左侧筛选 + 员工表，右侧权限编辑（对接 v3 `employee` / `employee_class`，行为对齐 RBP2 staff-setting）
 */
export default function JobSettingsPage() {
    const [employeeClasses, setEmployeeClasses] = useState<JobSettingsEmployeeClassRow[]>([]);
    const [employees, setEmployees] = useState<JobSettingsEmployeeRow[]>([]);
    const [listLoading, setListLoading] = useState(true);
    const employeeRawByIdRef = useRef<Map<string, Record<string, unknown>>>(new Map());

    const [roleFilter, setRoleFilter] = useState<JobSettingsRoleFilterValue>('all');
    const [selectedId, setSelectedId] = useState<string>('');
    const [roleValue, setRoleValue] = useState<string>('');
    const [personalSupplement, setPersonalSupplement] = useState<boolean>(false);
    /** 个人权限矩阵表单态；与模板一致时为 `null` */
    const [personalPermissionOverride, setPersonalPermissionOverride] =
        useState<EmployeePermissionClassRow | null>(null);
    const [exceptionDialogOpen, setExceptionDialogOpen] = useState(false);
    const [employeeInfo, setEmployeeInfo] = useState<JobSettingsEmployeeInfo>(() => createEmptyJobSettingsEmployeeInfo());
    const draftSnapshotRef = useRef<JobSettingsDraftSnapshot | null>(null);
    /** 用户点「取消」关闭右侧后，勿自动再选中第一项，直至其再次点选列表或切换顶筛 */
    const detailDismissedRef = useRef(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    /** 「新增员工」弹窗（Figma 7325:108873） */
    const [addEmployeeDialogOpen, setAddEmployeeDialogOpen] = useState(false);
    /** 「编辑员工」弹窗（与新增同款 + 只读编号/创建时间，Figma 7714:110277） */
    const [editEmployeeRowId, setEditEmployeeRowId] = useState<string | null>(null);
    const editEmployeeRow = useMemo(
        () => (editEmployeeRowId != null ? employees.find((r) => r.id === editEmployeeRowId) ?? null : null),
        [employees, editEmployeeRowId],
    );
    const saveBusyRef = useRef(false);
    const [saveSubmitting, setSaveSubmitting] = useState(false);
    const [addEmployeeSubmitting, setAddEmployeeSubmitting] = useState(false);
    const [editEmployeeSubmitting, setEditEmployeeSubmitting] = useState(false);
    const [deleteSubmitting, setDeleteSubmitting] = useState(false);

    const filterTabs = useMemo((): StackedFilterTabItem<string>[] => {
        const rest: StackedFilterTabItem<string>[] = employeeClasses
            .filter((c) => c.employee_class_id != null)
            .map((c) => ({
                id: String(c.employee_class_id),
                labelText: c.employee_class_name ?? '',
            }));
        return [{ id: 'all', labelI18nKey: 'job_settings_filter_all' }, ...rest];
    }, [employeeClasses]);

    const roleSelectItems = useMemo(
        () =>
            employeeClasses
                .filter((c) => c.employee_class_id != null)
                .map((c) => ({
                    value: String(c.employee_class_id),
                    label: c.employee_class_name ?? '',
                })),
        [employeeClasses],
    );

    /** 当前岗位下拉对应的权限模板（与权限设置页同源接口） */
    const rolePermissionTemplate = useMemo(
        () => findEmployeeClassTemplateByRoleValue(roleValue, employeeClasses),
        [employeeClasses, roleValue],
    );

    /** 例外项：模板与生效权限 diff（岗位切换会重算，不清空 override） */
    const exceptions = useMemo(
        () => computeJobSettingsExceptions(personalSupplement, personalPermissionOverride, rolePermissionTemplate),
        [personalSupplement, personalPermissionOverride, rolePermissionTemplate],
    );

    /** 当前岗位权限 Chip：最终生效权限 */
    const effectivePermissionChips = useMemo(() => {
        const effective = resolveEffectivePermissionApiRow(
            personalSupplement,
            personalPermissionOverride,
            rolePermissionTemplate,
        );
        return listEnabledPermissionDefsForChips(effective).map((d) => ({
            key: d.key,
            labelKey: d.labelKey,
        }));
    }, [personalSupplement, personalPermissionOverride, rolePermissionTemplate]);

    /** 新增弹窗默认岗位：首个员工等级 */
    const defaultAddEmployeeRoleValue = useMemo(() => {
        const firstClass = employeeClasses.find((c) => c.employee_class_id != null);
        return firstClass?.employee_class_id != null ? String(firstClass.employee_class_id) : '';
    }, [employeeClasses]);

    /** 无员工时可编辑 AU；有员工时与详情规则一致（任一行五项 AU 为 number 则可写） */
    const addEmployeeAuPatchable = useMemo(() => {
        if (employees.length === 0) {
            return true;
        }
        return employees.some((e) => e.auLoginFieldsPatchable);
    }, [employees]);

    const loadEmployees = useCallback(async (): Promise<JobSettingsEmployeeRow[] | null> => {
        setListLoading(true);
        try {
            const [empRes, clsRes] = await Promise.all([
                apiManage_rbp.get_employee_information(),
                apiManage_rbp.get_employee_permission_setting(),
            ]);
            if (isApiError(empRes) || isApiError(clsRes)) {
                GlobalNotification.error(i18n.t('request_failure_text_service_is_not_available'));
                return null;
            }
            const classes = Array.isArray(clsRes) ? (clsRes as JobSettingsEmployeeClassRow[]) : [];
            setEmployeeClasses(classes);
            const classById = buildClassNameByIdMap(classes);

            const list = Array.isArray(empRes) ? empRes : [];
            const rows: JobSettingsEmployeeRow[] = [];
            const rawMap = new Map<string, Record<string, unknown>>();
            for (const item of list) {
                if (!item || typeof item !== 'object') {
                    continue;
                }
                const { row, raw } = apiEmployeeToRow(item as Record<string, unknown>, classById);
                rows.push(row);
                rawMap.set(row.id, raw);
            }
            employeeRawByIdRef.current = rawMap;
            setEmployees(rows);
            return rows;
        } finally {
            setListLoading(false);
        }
    }, []);

    useEffect(() => {
        void loadEmployees();
    }, [loadEmployees]);

    /** 编辑弹窗打开期间若列表刷新导致该行不存在，关闭弹窗避免空快照 */
    useEffect(() => {
        if (editEmployeeRowId != null && !employees.some((r) => r.id === editEmployeeRowId)) {
            setEditEmployeeRowId(null);
        }
    }, [employees, editEmployeeRowId]);

    const visibleRows = useMemo(() => {
        if (roleFilter === 'all') {
            return employees;
        }
        return employees.filter((r) => String(r.employeeClassId) === roleFilter);
    }, [employees, roleFilter]);

    const selectedRow = useMemo(() => visibleRows.find((r) => r.id === selectedId), [visibleRows, selectedId]);

    useEffect(() => {
        if (!visibleRows.length) {
            return;
        }
        if (selectedId === '') {
            return;
        }
        if (!visibleRows.some((r) => r.id === selectedId)) {
            const first = visibleRows[0];
            const nextRoleValue = resolveJobSettingsRoleValueForSelect(first.defaultRoleValue, roleSelectItems);
            setSelectedId(first.id);
            setRoleValue(nextRoleValue);
            const nextOverride = clonePersonalPermissionOverride(first);
            setPersonalSupplement(first.defaultPersonalSupplement);
            setPersonalPermissionOverride(nextOverride);
            const nextInfo = cloneJobSettingsEmployeeInfo(first.employeeInfoDefaults);
            setEmployeeInfo(nextInfo);
            draftSnapshotRef.current = {
                roleValue: nextRoleValue,
                personalSupplement: first.defaultPersonalSupplement,
                personalPermissionOverride: nextOverride ? _.cloneDeep(nextOverride) : null,
                employeeInfo: cloneJobSettingsEmployeeInfo(nextInfo),
            };
        }
    }, [visibleRows, selectedId, roleSelectItems]);

    const handleSelectRow = useCallback((row: JobSettingsEmployeeRow) => {
        detailDismissedRef.current = false;
        setSelectedId(row.id);
        const nextRoleValue = resolveJobSettingsRoleValueForSelect(row.defaultRoleValue, roleSelectItems);
        setRoleValue(nextRoleValue);
        const nextOverride = clonePersonalPermissionOverride(row);
        setPersonalSupplement(row.defaultPersonalSupplement);
        setPersonalPermissionOverride(nextOverride);
        const nextInfo = cloneJobSettingsEmployeeInfo(row.employeeInfoDefaults);
        setEmployeeInfo(nextInfo);
        draftSnapshotRef.current = {
            roleValue: nextRoleValue,
            personalSupplement: row.defaultPersonalSupplement,
            personalPermissionOverride: nextOverride ? _.cloneDeep(nextOverride) : null,
            employeeInfo: cloneJobSettingsEmployeeInfo(nextInfo),
        };
    }, [roleSelectItems]);

    /** 有数据且当前无选中时，默认选中可见列表第一项（排除「取消」关闭详情后的状态） */
    useEffect(() => {
        if (listLoading) {
            return;
        }
        if (detailDismissedRef.current) {
            return;
        }
        if (!visibleRows.length || selectedId !== '') {
            return;
        }
        const first = visibleRows[0];
        handleSelectRow(first);
    }, [listLoading, visibleRows, selectedId, handleSelectRow]);

    const handleRoleFilterChange = useCallback((id: string) => {
        detailDismissedRef.current = false;
        setRoleFilter(id as JobSettingsRoleFilterValue);
    }, []);

    const handleRemoveException = useCallback(
        (permissionKey: string) => {
            setPersonalPermissionOverride((prev) =>
                revertPermissionKeyToTemplate(prev, rolePermissionTemplate, permissionKey),
            );
        },
        [rolePermissionTemplate],
    );

    const handleAddException = useCallback(() => {
        if (roleSelectItems.length === 0) {
            GlobalNotification.warning(i18n.t('permission_settings_empty_list'));
            return;
        }
        if (roleValue === '' || rolePermissionTemplate == null) {
            GlobalNotification.warning(i18n.t('job_settings_personal_permission_need_role'));
            return;
        }
        setExceptionDialogOpen(true);
    }, [roleSelectItems.length, roleValue, rolePermissionTemplate]);

    const handleExceptionDialogConfirm = useCallback(
        (form: EmployeePermissionClassRow) => {
            setPersonalPermissionOverride(
                isOverrideFormEqualToTemplate(form, rolePermissionTemplate) ? null : form,
            );
        },
        [rolePermissionTemplate],
    );

    const handleCancel = useCallback(() => {
        const snap = draftSnapshotRef.current;
        if (!snap) {
            return;
        }
        detailDismissedRef.current = true;
        setRoleValue(snap.roleValue);
        setPersonalSupplement(snap.personalSupplement);
        setPersonalPermissionOverride(
            snap.personalPermissionOverride ? _.cloneDeep(snap.personalPermissionOverride) : null,
        );
        setEmployeeInfo(cloneJobSettingsEmployeeInfo(snap.employeeInfo));
        setSelectedId('');
    }, []);

    const handleSave = useCallback(async () => {
        if (!selectedRow || saveBusyRef.current) {
            return;
        }
        if (!employeeInfo.employeeLastName.trim()) {
            GlobalNotification.error(i18n.t('staff_setting_page_please_fill_in_the_employee_name'));
            return;
        }
        const raw = employeeRawByIdRef.current.get(selectedRow.id);
        if (!raw) {
            GlobalNotification.error(i18n.t('request_failure_text_service_is_not_available'));
            return;
        }
        saveBusyRef.current = true;
        setSaveSubmitting(true);
        try {
            const merged: Record<string, unknown> = {
                ...raw,
                ...jobSettingsFormToApiPatch(employeeInfo, roleValue, selectedRow.auLoginFieldsPatchable),
            };
            const body = stripEmployeePatchPayload(merged);
            applyPersonalPermissionPatchToBody(
                body,
                personalSupplement,
                personalPermissionOverride,
                rolePermissionTemplate,
            );
            const res = await apiManage_rbp.patch_employee_information(Number(selectedRow.employeeId), body);
            if (isApiError(res)) {
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
                return;
            }
            GlobalNotification.success(i18n.t('global_text_operate_success'));
            draftSnapshotRef.current = {
                roleValue,
                personalSupplement,
                personalPermissionOverride: personalPermissionOverride
                    ? _.cloneDeep(personalPermissionOverride)
                    : null,
                employeeInfo: cloneJobSettingsEmployeeInfo(employeeInfo),
            };
            await loadEmployees();
        } finally {
            saveBusyRef.current = false;
            setSaveSubmitting(false);
        }
    }, [
        selectedRow,
        employeeInfo,
        roleValue,
        personalSupplement,
        personalPermissionOverride,
        rolePermissionTemplate,
        loadEmployees,
    ]);

    const confirmDeleteAccount = useCallback(async () => {
        if (!selectedRow) {
            setDeleteDialogOpen(false);
            return;
        }
        setDeleteSubmitting(true);
        try {
            const res = await apiManage_rbp.delete_employee_information(Number(selectedRow.employeeId));
            if (isApiError(res)) {
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
                return;
            }
            GlobalNotification.success(i18n.t('global_text_operate_success'));
            setDeleteDialogOpen(false);
            setSelectedId('');
            await loadEmployees();
        } finally {
            setDeleteSubmitting(false);
        }
    }, [selectedRow, loadEmployees]);

    /** 打开新增员工弹窗（须存在含 `employee_class_id` 的等级） */
    const handleOpenAddEmployeeDialog = useCallback(() => {
        const firstClass = employeeClasses.find((c) => c.employee_class_id != null);
        if (firstClass?.employee_class_id == null) {
            GlobalNotification.warning(i18n.t('permission_settings_empty_list'));
            return;
        }
        setAddEmployeeDialogOpen(true);
    }, [employeeClasses]);

    /** 表格行「编辑员工」：打开与新增同结构的编辑弹窗 */
    const handleOpenEditEmployeeDialog = useCallback((row: JobSettingsEmployeeRow) => {
        setEditEmployeeRowId(row.id);
    }, []);

    /** 关闭编辑员工弹窗 */
    const handleCloseEditEmployeeDialog = useCallback(() => {
        setEditEmployeeRowId(null);
    }, []);

    /** 弹窗「确定」：提交 `post_employee_information` 并选中新行 */
    const handleConfirmAddEmployeeFromDialog = useCallback(
        async ({ info, roleValue }: { info: JobSettingsEmployeeInfo; roleValue: string }) => {
            const classNum = Number(roleValue);
            if (!Number.isFinite(classNum) || classNum <= 0) {
                GlobalNotification.warning(i18n.t('permission_settings_empty_list'));
                return;
            }
            const prevIds = new Set(employees.map((e) => e.id));
            setAddEmployeeSubmitting(true);
            try {
                const payload = jobSettingsFormToApiPost(info, roleValue, addEmployeeAuPatchable);
                const res = await apiManage_rbp.post_employee_information(payload);
                if (isApiError(res)) {
                    GlobalNotification.error(i18n.t('global_text_operate_failure'));
                    return;
                }
                GlobalNotification.success(i18n.t('global_text_operate_success'));
                setAddEmployeeDialogOpen(false);
                const rows = await loadEmployees();
                if (!rows?.length) {
                    return;
                }
                let target = rows.find((r) => !prevIds.has(r.id));
                if (!target) {
                    const createdId = readCreatedEmployeeIdFromPost(res);
                    if (createdId != null) {
                        target = rows.find((r) => r.id === createdId);
                    }
                }
                if (target) {
                    setRoleFilter('all');
                    handleSelectRow(target);
                }
            } finally {
                setAddEmployeeSubmitting(false);
            }
        },
        [employees, loadEmployees, handleSelectRow, addEmployeeAuPatchable],
    );

    /** 编辑弹窗「确定」：`patch_employee_information` 与右侧保存逻辑一致 */
    const handleConfirmEditEmployeeFromDialog = useCallback(
        async ({ info, roleValue }: { info: JobSettingsEmployeeInfo; roleValue: string }) => {
            const row = editEmployeeRow;
            if (!row) {
                handleCloseEditEmployeeDialog();
                return;
            }
            if (!info.employeeLastName.trim()) {
                GlobalNotification.error(i18n.t('staff_setting_page_please_fill_in_the_employee_name'));
                return;
            }
            const raw = employeeRawByIdRef.current.get(row.id);
            if (!raw) {
                GlobalNotification.error(i18n.t('request_failure_text_service_is_not_available'));
                return;
            }
            setEditEmployeeSubmitting(true);
            try {
                const merged: Record<string, unknown> = {
                    ...raw,
                    ...jobSettingsFormToApiPatch(info, roleValue, row.auLoginFieldsPatchable),
                };
                const body = stripEmployeePatchPayload(merged);
                const editRoleTemplate = findEmployeeClassTemplateByRoleValue(roleValue, employeeClasses);
                applyPersonalPermissionPatchToBody(
                    body,
                    row.defaultPersonalSupplement,
                    row.defaultPersonalPermissionOverride,
                    editRoleTemplate,
                );
                const res = await apiManage_rbp.patch_employee_information(Number(row.employeeId), body);
                if (isApiError(res)) {
                    GlobalNotification.error(i18n.t('global_text_operate_failure'));
                    return;
                }
                GlobalNotification.success(i18n.t('global_text_operate_success'));
                handleCloseEditEmployeeDialog();
                const rows = await loadEmployees();
                const refreshed = rows?.find((r) => r.id === row.id);
                if (refreshed) {
                    detailDismissedRef.current = false;
                    handleSelectRow(refreshed);
                }
            } finally {
                setEditEmployeeSubmitting(false);
            }
        },
        [editEmployeeRow, employeeClasses, loadEmployees, handleSelectRow, handleCloseEditEmployeeDialog],
    );

    return (
        <Box className="job-settings-page">
            <Box className="job-settings-page__main">
                <Box className="job-settings-page__left-column">
                    <StackedFilterTabs tabs={filterTabs} value={roleFilter} onChange={handleRoleFilterChange} />

                    <Box className="job-settings-page__list-panel">
                        <Box className="job-settings-page__list-header">
                            <Typography className="job-settings-page__list-header-cell job-settings-page__list-header-cell--id">
                                {i18n.t('staff_setting_page_employee_id')}
                            </Typography>
                            <Typography className="job-settings-page__list-header-cell job-settings-page__list-header-cell--name">
                                {i18n.t('staff_setting_page_employee_name')}
                            </Typography>
                            <Typography className="job-settings-page__list-header-cell job-settings-page__list-header-cell--perm">
                                {i18n.t('job_settings_col_employee_permission')}
                            </Typography>
                            <Box className="job-settings-page__list-header-cell job-settings-page__list-header-cell--edit" aria-hidden />
                        </Box>
                        <Divider sx={{ flexShrink: 0, borderColor: 'var(--stroke-neutral-800-a25)' }} />
                        <Box className="job-settings-page__list-body" aria-busy={listLoading}>
                            {listLoading && renderJobSettingsListSkeleton()}
                            {!listLoading && employees.length === 0 && (
                                <Typography sx={{ py: 4, px: 2, color: 'var(--text-secondary)', textAlign: 'center' }}>
                                    {i18n.t('general_text_no_data')}
                                </Typography>
                            )}
                            {!listLoading &&
                                visibleRows.map((row) => {
                                    const selected = row.id === selectedId;
                                    return (
                                        <Box key={row.id}>
                                            <Box
                                                className={
                                                    selected
                                                        ? 'job-settings-page__list-row-wrapper job-settings-page__list-row-wrapper--selected'
                                                        : 'job-settings-page__list-row-wrapper'
                                                }
                                            >
                                                <ButtonBase
                                                    type="button"
                                                    className="job-settings-page__list-row"
                                                    onClick={() => handleSelectRow(row)}
                                                >
                                                    <Typography component="span" className="job-settings-page__list-cell job-settings-page__list-cell--id">
                                                        {row.employeeId}
                                                    </Typography>
                                                    <Typography component="span" className="job-settings-page__list-cell job-settings-page__list-cell--name">
                                                        {row.displayName}
                                                    </Typography>
                                                    <Typography
                                                        component="span"
                                                        className="job-settings-page__list-cell job-settings-page__list-cell--perm"
                                                        sx={{ color: 'var(--text-primary-300)' }}
                                                    >
                                                        {row.roleDisplayName}
                                                    </Typography>
                                                </ButtonBase>
                                                <IconButton
                                                    type="button"
                                                    size="small"
                                                    aria-label={i18n.t('job_settings_edit_employee')}
                                                    disabled={listLoading || editEmployeeSubmitting}
                                                    onClick={() => handleOpenEditEmployeeDialog(row)}
                                                    sx={{
                                                        /** 与列表「员工权限」列同为语义主题字色；SVG 为 stroke=`currentColor` 非写死色 */
                                                        color: 'var(--text-primary-300)',
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    <TablerEditIcon width={22} height={22} aria-hidden />
                                                </IconButton>
                                            </Box>
                                            <Divider sx={{ borderColor: 'var(--stroke-neutral-800-a25)' }} />
                                        </Box>
                                    );
                                })}
                        </Box>
                        <LoadingOverlayButton
                            type="button"
                            variant="outlined"
                            fullWidth
                            className="job-settings-page__add-employee"
                            disabled={listLoading}
                            loading={addEmployeeSubmitting}
                            loadingIndicatorSize={22}
                            onClick={handleOpenAddEmployeeDialog}
                            endIcon={<PlusIcon width={28} height={28} style={{ color: 'var(--text-dark)' }} aria-hidden />}
                            sx={{ flexShrink: 0, mt: 0, textTransform: 'none' }}
                        >
                            {i18n.t('job_settings_add_employee')}
                        </LoadingOverlayButton>
                    </Box>
                </Box>

                {selectedRow ? (
                    <JobSettingsDetailPanel
                        roleSelectItems={roleSelectItems}
                        effectivePermissionChips={effectivePermissionChips}
                        selected={selectedRow}
                        roleValue={roleValue}
                        onRoleChange={setRoleValue}
                        personalSupplement={personalSupplement}
                        onPersonalSupplementChange={setPersonalSupplement}
                        exceptions={exceptions}
                        onRemoveException={handleRemoveException}
                        onAddException={handleAddException}
                        onDeleteAccount={() => setDeleteDialogOpen(true)}
                        onCancel={handleCancel}
                        saveSubmitting={saveSubmitting}
                        onSave={() => void handleSave()}
                    />
                ) : null}

            <JobSettingsExceptionPermissionDialog
                open={exceptionDialogOpen}
                onClose={() => setExceptionDialogOpen(false)}
                onConfirm={handleExceptionDialogConfirm}
                personalSupplement={personalSupplement}
                overrideForm={personalPermissionOverride}
                templateRow={rolePermissionTemplate}
            />
            </Box>

            {deleteDialogOpen && (
                <AlertDialog
                    open
                    variant="delete"
                    title={i18n.t('job_settings_delete_account')}
                    bodyPrompt={i18n.t('google_text_public_button_delete')}
                    highlightText={selectedRow?.displayName ?? ''}
                    confirmLoading={deleteSubmitting}
                    onCancel={() => setDeleteDialogOpen(false)}
                    onConfirm={() => void confirmDeleteAccount()}
                />
            )}

            <JobSettingsAddEmployeeDialog
                open={addEmployeeDialogOpen}
                onClose={() => setAddEmployeeDialogOpen(false)}
                roleSelectItems={roleSelectItems}
                defaultRoleValue={defaultAddEmployeeRoleValue}
                auLoginFieldsPatchable={addEmployeeAuPatchable}
                submitting={addEmployeeSubmitting}
                onConfirm={handleConfirmAddEmployeeFromDialog}
            />

            {editEmployeeRow ? (
                <JobSettingsAddEmployeeDialog
                    variant="edit"
                    open={Boolean(editEmployeeRow)}
                    onClose={handleCloseEditEmployeeDialog}
                    roleSelectItems={roleSelectItems}
                    defaultRoleValue=""
                    readOnlyEmployeeId={editEmployeeRow.employeeId}
                    readOnlyEmployeeCreatedAt={editEmployeeRow.employeeCreatedAtDisplay}
                    initialEmployeeInfo={editEmployeeRow.employeeInfoDefaults}
                    initialRoleValue={editEmployeeRow.defaultRoleValue}
                    auLoginFieldsPatchable={editEmployeeRow.auLoginFieldsPatchable}
                    submitting={editEmployeeSubmitting}
                    onConfirm={handleConfirmEditEmployeeFromDialog}
                />
            ) : null}
        </Box>
    );
}

const JOB_SETTINGS_LIST_SKELETON_ROWS = 6;

/** 列表加载骨架：与表头三列（编号 / 姓名 / 权限）同宽 */
function renderJobSettingsListSkeleton(): JSX.Element {
    return (
        <>
            {Array.from({ length: JOB_SETTINGS_LIST_SKELETON_ROWS }).map((_, i) => (
                <Box key={`job-settings-list-skeleton-${i}`}>
                    <Box
                        sx={{
                            display: 'flex',
                            gap: '32px',
                            alignItems: 'center',
                            width: '100%',
                            py: 1,
                            my: 0.5,
                        }}
                    >
                        <Skeleton variant="rounded" animation="wave" width={120} height={24} sx={{ borderRadius: '8px', flexShrink: 0 }} />
                        <Skeleton variant="rounded" animation="wave" width={200} height={24} sx={{ borderRadius: '8px', flexShrink: 0 }} />
                        <Skeleton
                            variant="rounded"
                            animation="wave"
                            height={24}
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                borderRadius: '8px',
                                maxWidth: i % 3 === 0 ? '92%' : i % 3 === 1 ? '78%' : '88%',
                            }}
                        />
                        <Skeleton variant="rounded" animation="wave" width={40} height={40} sx={{ borderRadius: '8px', flexShrink: 0 }} />
                    </Box>
                    {i < JOB_SETTINGS_LIST_SKELETON_ROWS - 1 ? (
                        <Divider sx={{ borderColor: 'var(--stroke-neutral-800-a25)' }} />
                    ) : null}
                </Box>
            ))}
        </>
    );
}